import {
  InvoiceAccountNoteViewResourceWithFakeRelationship,
  ManyIncludedInvoiceAccountNoteViewResourceWithFakeRelationshipUnion,
} from 'api-client'
import axios, { AxiosResponse, CancelToken } from 'axios'

import { request } from '~shared-components/api/request'
import { InvoiceFilter } from '~shared-components/filters/invoices.filters'
import {
  dateFilterToAPIQuery,
  FilterMap,
  mapFiltersToAPIQuery,
} from '~shared-components/filters/utils'
import {
  Invoice,
  TransformedInvoice,
  transformInvoice,
} from '~shared-components/transforms/invoice.transforms'
import { SortingType } from '~shared-components/translations/sorting'
import { deserialize } from '~shared-components/utils/deserialize'

const transformSort = (sort: string | undefined) => {
  if (sort === undefined) return undefined

  const transform: Partial<Record<SortingType, string>> = {
    '-currency': '-netAmount.currency',
    '-invoiceNumber': '-localNumber',
    '-netAmount': '-netAmount.amount',
    currency: 'netAmount.currency',
    invoiceNumber: 'localNumber',
    netAmount: 'netAmount.amount',
  }

  return transform[sort as SortingType] ?? sort
}

/**
 *  Any transforms that need to be done to the filter query
 *  before it gets passed along to the API should go here
 */
const formatFilters = ({
  dueDateGreaterEqual,
  dueDateLessEqual,
  issueDateGreaterEqual,
  issueDateLessEqual,
  sort,
  ...rest
}: FilterMap<typeof InvoiceFilter>) => ({
  ...rest,
  dueDateGreaterEqual: dateFilterToAPIQuery(dueDateGreaterEqual),
  dueDateLessEqual: dateFilterToAPIQuery(dueDateLessEqual),
  issueDateGreaterEqual: dateFilterToAPIQuery(issueDateGreaterEqual),
  issueDateLessEqual: dateFilterToAPIQuery(issueDateLessEqual),
  sort: transformSort(sort),
})

export interface FetchInvoicesParams {
  apiBaseUrl: string
  cancelToken?: CancelToken
  customerContextId?: string
  filters: FilterMap<typeof InvoiceFilter>
  include?: string
  limit?: number
  offset?: number
  salesGroupId?: string
}

interface FetchInvoceWithAcceptParams extends FetchInvoicesParams {
  acceptType: string | undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractFilenameFromHeaders = (headers: any) => {
  const matches = /filename="(.*?)"/.exec(headers['content-disposition']) ?? []
  return matches[1]
}

export interface FetchInvoicesJsonResponse {
  cancel?: boolean
  error?: boolean
  invoices: TransformedInvoice[]
  total: number
}
export interface FetchInvoicesCsvResponse {
  cancel?: boolean
  error?: boolean
  fileName: string
  invoices: string
}

const FETCH_INVOICES_JSON_ERROR: FetchInvoicesJsonResponse = {
  error: true,
  invoices: [],
  total: 0,
}

const FETCH_INVOICES_CSV_ERROR: FetchInvoicesCsvResponse = {
  error: true,
  fileName: '',
  invoices: '',
}

const FETCH_INVOICES_JSON_CANCEL: FetchInvoicesJsonResponse = {
  cancel: true,
  invoices: [],
  total: 0,
}

const FETCH_INVOICES_CSV_CANCEL: FetchInvoicesCsvResponse = {
  cancel: true,
  fileName: '',
  invoices: '',
}

async function transformJsonInvoices(
  responsePromise: Promise<
    AxiosResponse<ManyIncludedInvoiceAccountNoteViewResourceWithFakeRelationshipUnion>
  >,
): Promise<FetchInvoicesJsonResponse> {
  try {
    const response = await responsePromise
    const invoices = deserialize<Invoice[]>(response.data)

    return {
      invoices: invoices.map(transformInvoice),
      total: response.data?.meta?.count | 0,
    }
  } catch (e) {
    return axios.isCancel(e)
      ? FETCH_INVOICES_JSON_CANCEL
      : FETCH_INVOICES_JSON_ERROR
  }
}

async function transformCsvInvoices(
  responsePromise: Promise<AxiosResponse<string>>,
): Promise<FetchInvoicesCsvResponse> {
  try {
    const response = await responsePromise

    return {
      fileName: extractFilenameFromHeaders(response.headers),
      invoices: response.data,
    }
  } catch (e) {
    return axios.isCancel(e)
      ? FETCH_INVOICES_CSV_CANCEL
      : FETCH_INVOICES_CSV_ERROR
  }
}

const fetchInvoices = async <T>({
  acceptType,
  apiBaseUrl,
  cancelToken,
  customerContextId,
  filters,
  include,
  limit,
  offset,
  salesGroupId,
}: FetchInvoceWithAcceptParams): Promise<AxiosResponse<T>> =>
  request<T>({
    cancelToken,
    headers: {
      Accept: acceptType,
    },
    method: 'GET',
    params: {
      ...(customerContextId
        ? {
            'filter[delivery.order.customerContext.id.equals]': customerContextId,
          }
        : {}),
      'filter[delivery.order.customerContext.salesGroup.id.equals]': salesGroupId,
      include,
      'page[number]': offset,
      'page[size]': limit,
      ...mapFiltersToAPIQuery(formatFilters(filters), InvoiceFilter),
    },
    timeout: 60000,
    url: `${apiBaseUrl}/api/invoice-account-notes`,
  })

export const fetchInvoicesCsv = async (
  params: FetchInvoicesParams,
): Promise<FetchInvoicesCsvResponse> => {
  const responsePromise = fetchInvoices<string>({
    ...params,
    acceptType: 'text/csv',
  })
  return transformCsvInvoices(responsePromise)
}

export const fetchInvoicesJson = async (
  params: FetchInvoicesParams,
): Promise<FetchInvoicesJsonResponse> => {
  const responsePromise = fetchInvoices<ManyIncludedInvoiceAccountNoteViewResourceWithFakeRelationshipUnion>(
    { ...params, acceptType: 'application/json' },
  )
  return transformJsonInvoices(responsePromise)
}

export const fetchInvoice = async (
  baseUrl: string,
  id: string,
  customerContextId: string,
): Promise<TransformedInvoice> => {
  const response = await request<InvoiceAccountNoteViewResourceWithFakeRelationship>(
    {
      headers: {
        Accept: 'application/json',
      },
      method: 'GET',
      params: {
        'filter[delivery.order.customerContext.id.equals]': customerContextId,
        include: 'delivery.order.customerContext',
      },
      url: `${baseUrl}/api/invoice-account-notes/${id}`,
    },
  )

  const invoice = deserialize<Invoice>(response.data)

  return transformInvoice(invoice)
}
